import type { CSSProperties, FC, ReactNode } from 'react'

import { useRef } from 'react'
import { Camera, Check, FileText, TriangleAlert, WifiOff } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useScrollProgress } from '@/hooks/use-scroll-progress'
import { SectionLabel } from '@/components/generals/section-label'

type Answer = 'ok' | 'feil'

const ITEMS: Array<{ no: string, text: string, answer: Answer }> = [
    { no: '1.1', text: 'Er detaljerte tegninger og beskrivelser av arbeidet iht. kravene?', answer: 'ok' },
    { no: '1.2', text: 'Er det angitt samme toleranseklasse for underlag og overflater?', answer: 'ok' },
    { no: '1.5', text: 'Er materialene anbefalt for formålet?', answer: 'ok' },
    { no: '1.6', text: 'Er materialene iht. bestilling og uten feil?', answer: 'feil' },
    { no: '2.3', text: 'Er fall mot sluk minst 1:50 i dusj, og 1:100 for øvrig?', answer: 'ok' },
    { no: '2.4', text: 'Er sluket plassert minst 300 mm fra vegg?', answer: 'ok' }
]

const SIGNATURE = 'M10 70 C20 30 40 20 45 50 S30 90 55 60 S80 20 85 55 C88 75 95 75 100 55 C104 40 110 40 112 60 C114 78 122 70 126 52 L128 40 C130 60 132 76 140 66 C150 50 160 38 168 52 C174 64 166 78 178 68 C188 58 196 44 204 58 C210 70 218 72 228 56 C236 44 246 46 250 60 C254 72 264 70 290 40'
const SIGN_LINE = 'M58 88 C120 82 200 84 272 76'

const POINTS = [
    { icon: WifiOff, strong: 'Fungerer uten dekning', rest: 'og laster opp når nettet er tilbake' },
    { icon: TriangleAlert, strong: 'Avvik opprettes direkte', rest: 'fra punktet det gjelder' },
    { icon: Camera, strong: 'Legg ved bilder', rest: 'til hvert sjekklistepunkt ved behov' },
    { icon: FileText, strong: 'Komplett KS-dokumentasjon som PDF', rest: 'klar til kunde eller tilsyn' }
]

/** A wet-room checklist filled in by scrolling: answers tick, a deviation is logged, the foreman signs. */
export const ChecklistScene: FC = (): ReactNode => {
    const track = useRef<HTMLElement>(null)
    const sheet = useRef<HTMLDivElement>(null)

    useScrollProgress(track, { startAt: 0, endAt: 1 })

    useScrollProgress(sheet, {
        startAt: 0.95,
        endAt: 0.3,
        varName: null,
        onUpdate: progress => {
            const el = sheet.current

            if (!el) return

            if (window.matchMedia('(min-width: 1024px) and (min-height: 760px) and (prefers-reduced-motion: no-preference)').matches) el.style.removeProperty('--p')
            else el.style.setProperty('--p', progress.toFixed(4))
        }
    })

    return (
        <section ref={track} id='utfylling' className='check-track tone-night relative' data-header='dark'>
            <div className='check-stage grain'>
                <div className='grid-lines' />

                <div className='wrap relative grid h-full grid-cols-12 content-center gap-x-6 gap-y-12 pt-[var(--header-h)] pb-8'>
                    <div className='col-span-12 lg:col-span-5'>
                        <SectionLabel index='02'>Utfylling</SectionLabel>
                        <h2 className='t-h1 mt-5 balance'>Dokumentér hvert steg, rett fra mobilen.</h2>
                        <p className='mt-6 max-w-md text-tone-muted pretty'>
                            Hele laget fyller ut sjekklistene der jobben gjøres. Start og gjenoppta når det passer, og opprett avvik direkte fra sjekklisten.
                        </p>

                        <ul className='mt-10 flex flex-col gap-4'>
                            {POINTS.map(point => {
                                const Icon = point.icon

                                return (
                                    <li key={point.strong} className='flex items-start gap-4 text-tone-muted'>
                                        <span className='flex size-8 shrink-0 items-center justify-center border border-tone-line text-signal'>
                                            <Icon className='size-4' aria-hidden='true' />
                                        </span>
                                        <span className='pt-1'>
                                            <strong className='font-semibold text-tone-fg'>{point.strong}</strong> {point.rest}
                                        </span>
                                    </li>
                                )
                            })}
                        </ul>
                    </div>

                    <div className='col-span-12 lg:col-span-6 lg:col-start-7'>
                        <div ref={sheet} className='check-sheet relative bg-chalk text-night shadow-[0_50px_100px_-40px_rgb(0_0_0/0.7)]'>
                            <div className='flex items-start justify-between gap-6 border-b border-steel px-5 pt-5 pb-4 md:px-7 md:pt-6'>
                                <div>
                                    <p className='t-label text-slate'>06-S010 · 06 Tømrer</p>
                                    <p className='mt-2 text-lg font-semibold leading-tight tracking-tight md:text-xl'>Utførelse av tømrerarbeid i våtrom</p>
                                </div>
                                <span className='check-progress t-label shrink-0 bg-night px-2 py-1 text-paper tabular' aria-hidden='true'>
                                    <span className='check-progress-done'>6/6</span>
                                </span>
                            </div>

                            <ol className='px-5 md:px-7'>
                                {ITEMS.map((item, i) => {
                                    const s = (0.08 + i * 0.09).toFixed(3)

                                    return (
                                        <li key={item.no} className='check-item border-b border-steel/60 py-3.5' style={{ '--s': s } as CSSProperties}>
                                            <div className='flex items-start gap-3'>
                                                <span className='t-label tabular w-8 shrink-0 pt-1 text-slate'>{item.no}</span>
                                                <p className='flex-1 text-sm font-medium leading-snug'>{item.text}</p>
                                            </div>

                                            <div className='mt-2.5 flex gap-2 pl-11' aria-hidden='true'>
                                                <span className={cn('check-chip', item.answer === 'ok' && 'is-picked is-ok')}>
                                                    {item.answer === 'ok' && <Check className='size-3' strokeWidth={3} />}
                                                    OK
                                                </span>
                                                <span className={cn('check-chip', item.answer === 'feil' && 'is-picked is-feil')}>Feil</span>
                                                <span className='check-chip'>IR</span>
                                            </div>

                                            {item.answer === 'feil' && (
                                                <p className='check-avvik mt-2.5 ml-11 flex items-center gap-2 bg-butter px-2.5 py-1.5 text-xs font-semibold'>
                                                    <TriangleAlert className='size-3.5 text-ember' aria-hidden='true' />
                                                    Avvik opprettet
                                                    <span className='ml-auto flex items-center gap-1 font-normal text-slate'>
                                                        <Camera className='size-3.5' aria-hidden='true' /> 1 bilde
                                                    </span>
                                                </p>
                                            )}
                                        </li>
                                    )
                                })}
                            </ol>

                            <div className='grid grid-cols-[1fr_auto] items-end gap-4 px-5 pt-4 pb-5 md:px-7 md:pb-6'>
                                <div>
                                    <svg viewBox='0 0 300 100' className='h-16 w-full max-w-[16rem] md:h-20' aria-hidden='true'>
                                        <path d={SIGNATURE} pathLength={1} className='check-sign' fill='none' stroke='#241D2D' strokeWidth='2.6' strokeLinecap='round' strokeLinejoin='round' />
                                        <path d={SIGN_LINE} pathLength={1} className='check-sign check-sign-line' fill='none' stroke='#F96529' strokeWidth='2' strokeLinecap='round' />
                                    </svg>
                                    <p className='text-xs text-slate'>Ola Hansen · Bas</p>
                                </div>

                                <p className='check-signed t-label flex items-center gap-2 bg-lime px-2.5 py-1.5 text-night'>
                                    <Check className='size-3.5' strokeWidth={3} aria-hidden='true' />
                                    Signert av bas
                                </p>
                            </div>
                        </div>

                        <p className='t-label mt-5 text-tone-muted'>Sjekklisten er hentet fra malbiblioteket i Svenn. Over 1000 bransjemaler for rehab, våtrom og nybygg (TEK17).</p>
                    </div>
                </div>
            </div>
        </section>
    )
}
